import { isNotDefined } from '@sgme/fp';
import { HStack } from '@sgme/ui';
import type { CustomCellRendererProps } from 'ag-grid-react';
import deepEqual from 'fast-deep-equal';
import { useCallback } from 'react';

import { OpenInXoneButton } from '@/App/Blotter/grids/OpenInXoneButton.tsx';
import { RepriceQuoteButton } from '@/App/Blotter/grids/RepriceQuoteButton.tsx';
import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import type { BlotterOptionEntry } from '@/store/blotter/types/BlotterOptionEntry.ts';
import { optionRepriceQuote } from '@/store/blotter/workflows/cashRepriceQuote/action.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';

export const CommandsCellRenderer = ({ data: entry }: CustomCellRendererProps<BlotterOptionEntry, string>) => {
  const dispatch = useAppDispatch();
  const track = useTrackEvent();
  const userPreferences = useAppSelector((state) => state.userPreferences);
  const currentTabId = useAppSelector((state) => state.tabs.activeTabId);
  const children = useAppSelector(
    (state) => {
      if (state.blotter.status === 'registered') {
        return Object.values(state.blotter.entries.Option).filter((e) => e?.strategyReference === entry?.id);
      }
      return [];
    },
    { equalityFn: deepEqual },
  );

  const onRepriceQuoteClick = useCallback(() => {
    if (isNotDefined(entry)) {
      return;
    }
    dispatch(optionRepriceQuote(entry, children, currentTabId, userPreferences));
    track('Reprice quote for option', entry.id);
  }, [entry, children, currentTabId, userPreferences]);

  if (isNotDefined(entry) || isNotDefined(entry.product)) {
    return null;
  }

  const canReprice =
    // cannot reprice an 'internal leg' of a complex option, it must be repriced as a whole
    (isNotDefined(entry?.strategyReference) || entry.strategyReference === '') && entry.venue === 'SG-MARKETS';

  return (
    <HStack horizontalAlignment="start">
      <OpenInXoneButton entry={entry} entryType="option" />
      {canReprice && <RepriceQuoteButton entry={entry} onClick={onRepriceQuoteClick} />}
    </HStack>
  );
};
